﻿import i18next from 'i18next';
import { toast } from 'sonner';
import { usePendingChangeStore } from '../../store/pending-change.store.js';
import { skipNextMyWorkRefetch } from '../../store/my-work-sync.store.js';
import { Logger } from '@/lib/app-log.js';

const MY_WORK_MUTATION_SKIP_TABS = [
    'tasks',
    'my-submissions',
    'my-involvement',
    'my-informed',
];

/**
 * MyWorkIntegration â€” drives My Work tabs (unified pending list, submissions,
 * involvement, informed) from the `/my-work/*` endpoints.
 *
 * Each row from the server carries
 *   `kind: 'workflowTask' | 'workflowSubmission' | 'documentRevision'`
 * and normalized resourceName/operation/pageKey so UI components (table,
 * dialog) render every kind uniformly. Actions (approve/reject/return) are
 * dispatched on the same endpoint by kind â€” the server delegates to the
 * correct model + engine path, keeping a single source of truth.
 */
export class MyWorkIntegration {
    constructor(sdkService) {
        this.service = sdkService;
    }

    /**
     * @param {Record<string, unknown>} [params]
     * @param {{ silent?: boolean }} [options] â€” silent: no loading spinner / error toast (background sync after workflow decide)
     */
    async fetchMyPending(params = {}, options = {}) {
        const { silent = false } = options;
        const store = usePendingChangeStore.getState();
        if (!silent) {
            store.setLoading(true);
            store.clearError();
        }

        try {
            const FILTER_KEYS = ['operation', 'pageKey', 'inboxRole', 'status'];
            const merged = { ...store.searchParams, ...params };
            const effectiveParams = {
                page: Number(merged.page) || 1,
                pageSize: Number(merged.pageSize) || 15,
                sortBy: merged.sortBy || 'createdAt',
                sortOrder: merged.sortOrder || 'desc',
                search: typeof merged.search === 'string' ? merged.search : '',
            };
            for (const key of FILTER_KEYS) {
                if (merged[key]) effectiveParams[key] = merged[key];
            }
            store.replaceSearchParams(effectiveParams);
            const response = await this.service.getMyPending(effectiveParams);

            const rows = Array.isArray(response.data)
                ? response.data
                : (Array.isArray(response.data?.data) ? response.data.data : []);
            store.setItems(rows);
            const meta = response.meta || response.pagination;
            if (meta && (meta.total != null || meta.page != null)) {
                store.setPaginationData(meta);
            }
            if (response.uniqueValues) store.setUniqueValues(response.uniqueValues);
            if (import.meta.env.DEV) {
                Logger.debug('[MyWork] fetchMyPending', {
                    total: rows.length,
                    workflowTasks: rows.filter((r) => r.kind === 'workflowTask').length,
                    kinds: [...new Set(rows.map((r) => r.kind))],
                    params: effectiveParams,
                });
            }
            return { success: true, data: rows };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (!silent) {
                store.setError(message);
                toast.error(message);
            }
            return { success: false, error: message };
        } finally {
            if (!silent) store.setLoading(false);
        }
    }

    async fetchMySubmissions(params = {}, options = {}) {
        const { silent = false } = options;
        const store = usePendingChangeStore.getState();
        if (!silent) {
            store.setSubmissionsLoading(true);
            store.clearSubmissionsError();
        }

        try {
            const effectiveParams = { ...store.submissionsSearchParams, ...params };
            store.setSubmissionsSearchParams(params);
            const response = await this.service.getMySubmissions(effectiveParams);

            store.setSubmissionsItems(response.data || []);
            if (response.meta) store.setSubmissionsPaginationData(response.meta);
            if (response.uniqueValues) store.setSubmissionsUniqueValues(response.uniqueValues);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (!silent) {
                store.setSubmissionsError(message);
                toast.error(message);
            }
            return { success: false, error: message };
        } finally {
            if (!silent) store.setSubmissionsLoading(false);
        }
    }

    async fetchMyApprovalInvolvement(params = {}, options = {}) {
        const { silent = false } = options;
        const store = usePendingChangeStore.getState();
        if (!silent) {
            store.setInvolvementLoading(true);
            store.clearInvolvementError();
        }

        try {
            const effectiveParams = { ...store.involvementSearchParams, ...params };
            store.setInvolvementSearchParams(params);
            const response = await this.service.getMyApprovalInvolvement(effectiveParams);

            store.setInvolvementItems(response.data || []);
            if (response.meta) store.setInvolvementPaginationData(response.meta);
            if (response.uniqueValues) store.setInvolvementUniqueValues(response.uniqueValues);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (!silent) {
                store.setInvolvementError(message);
                toast.error(message);
            }
            return { success: false, error: message };
        } finally {
            if (!silent) store.setInvolvementLoading(false);
        }
    }

    async fetchMyInformed(params = {}, options = {}) {
        const { silent = false } = options;
        const store = usePendingChangeStore.getState();
        if (!silent) {
            store.setInformedLoading(true);
            store.clearInformedError();
        }

        try {
            const effectiveParams = { ...store.informedSearchParams, ...params };
            store.setInformedSearchParams(params);
            const response = await this.service.getMyInformed(effectiveParams);

            store.setInformedItems(response.data || []);
            if (response.meta) store.setInformedPaginationData(response.meta);
            if (response.uniqueValues) store.setInformedUniqueValues(response.uniqueValues);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            if (!silent) {
                store.setInformedError(message);
                toast.error(message);
            }
            return { success: false, error: message };
        } finally {
            if (!silent) store.setInformedLoading(false);
        }
    }

    /**
     * Resolves (kind, id) from either an explicit `kind` argument or an item
     * object that already carries a `kind` field (as returned by /my-work/*).
     */
    _resolveKindId(kindOrItem, maybeId) {
        if (typeof kindOrItem === 'string') {
            return { kind: kindOrItem, id: String(maybeId || '') };
        }
        if (kindOrItem && typeof kindOrItem === 'object') {
            const kind = kindOrItem.kind || 'workflowTask';
            const id = kindOrItem._id || kindOrItem.id;
            return { kind, id: String(id || '') };
        }
        return { kind: 'workflowTask', id: '' };
    }

    /**
     * Optimistic list update + silent refetch of all My Work tabs after approve/reject/return.
     * @param {string|object} kindOrItem
     * @param {{ delayedRefetchMs?: number }} [options]
     */
    async _refreshAfterMutation(kindOrItem, options = {}) {
        const { id } = this._resolveKindId(kindOrItem);
        const store = usePendingChangeStore.getState();
        if (id) store.removeItem(id);

        skipNextMyWorkRefetch(MY_WORK_MUTATION_SKIP_TABS);

        await Promise.all([
            this.fetchMyPending({ page: 1 }, { silent: true }),
            this.fetchMySubmissions({}, { silent: true }),
            this.fetchMyApprovalInvolvement({}, { silent: true }),
            this.fetchMyInformed({}, { silent: true }),
        ]);

        // Instance is constructed in the registry; the service module only exports the class.
        const { workflowTaskIntegration } = await import('../api/integrations-registry.js');
        await workflowTaskIntegration.fetchMyInbox({}, { silent: true });

        const delayMs = options.delayedRefetchMs ?? 400;
        if (delayMs > 0) {
            setTimeout(() => {
                void this.fetchMyPending({ page: 1 }, { silent: true });
            }, delayMs);
        }
    }

    async approve(kindOrItem, idOrData = {}, maybeData = {}) {
        const store = usePendingChangeStore.getState();
        const { kind, id } = this._resolveKindId(kindOrItem, typeof idOrData === 'string' ? idOrData : null);
        const data = typeof idOrData === 'string' ? maybeData : idOrData;
        store.setLoading(true);
        skipNextMyWorkRefetch(MY_WORK_MUTATION_SKIP_TABS);

        try {
            const response = await this.service.approve(kind, id, data);
            toast.success(i18next.t('integrationToasts:approved', { defaultValue: 'Approved' }));
            store.closeApproveDialog();
            await this._refreshAfterMutation(kindOrItem);
            return { success: true, data: response.data, flowComplete: response.flowComplete };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async reject(kindOrItem, idOrData = {}, maybeData = {}) {
        const store = usePendingChangeStore.getState();
        const { kind, id } = this._resolveKindId(kindOrItem, typeof idOrData === 'string' ? idOrData : null);
        const data = typeof idOrData === 'string' ? maybeData : idOrData;
        store.setLoading(true);
        skipNextMyWorkRefetch(MY_WORK_MUTATION_SKIP_TABS);

        try {
            const response = await this.service.reject(kind, id, data);
            toast.success(i18next.t('integrationToasts:rejected', { defaultValue: 'Rejected' }));
            store.closeRejectDialog();
            await this._refreshAfterMutation(kindOrItem);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async returnToSubmitter(kindOrItem, idOrData = {}, maybeData = {}) {
        const store = usePendingChangeStore.getState();
        const { kind, id } = this._resolveKindId(kindOrItem, typeof idOrData === 'string' ? idOrData : null);
        const data = typeof idOrData === 'string' ? maybeData : idOrData;
        store.setLoading(true);
        skipNextMyWorkRefetch(MY_WORK_MUTATION_SKIP_TABS);

        try {
            const response = await this.service.returnToSubmitter(kind, id, data);
            toast.success(i18next.t('integrationToasts:sentBack', { defaultValue: 'Sent back' }));
            await this._refreshAfterMutation(kindOrItem);
            return { success: true, data: response.data };
        } catch (error) {
            const message = this._getErrorMessage(error);
            toast.error(message);
            return { success: false, error: message };
        } finally {
            store.setLoading(false);
        }
    }

    async fetchByKindAndId(kind, id) {
        try {
            const response = await this.service.getByKindAndId(kind, id);
            return { success: true, data: response.data };
        } catch (error) {
            return { success: false, error: this._getErrorMessage(error) };
        }
    }

    /** Non-asset gated drafts (Document/Action) â€” workflow v2 uses instance/task APIs when wired. */
    async fetchGatedDraftById() {
        return { success: false, data: null };
    }

    async fetchGatedDraftsByResource() {
        return { success: true, data: [] };
    }

    async fetchGatedDraftOpenSummary() {
        return { success: true, data: {} };
    }

    /**
     * Notification deep-link: locate a pending row page then load that page.
     * @param {{ entityId?: string, pageSize?: number, listQuery?: object }} [params]
     */
    async locateRowInList({ entityId, pageSize = 15, listQuery = {} } = {}) {
        if (!entityId) return { found: false };

        const focusRes = await this.service.locateInList?.({
            focusEntityId: String(entityId),
            pageSize: pageSize || 15,
        });
        const focus = focusRes?.focus;
        if (!focus?.found || !focus.page) {
            return { found: false };
        }

        const store = usePendingChangeStore.getState();
        const effectiveParams = {
            ...store.searchParams,
            page: focus.page,
            pageSize: pageSize || 15,
            search: listQuery.search ?? store.searchParams.search ?? '',
            ...(listQuery.extraQuery || {}),
        };
        await this.fetchMyPending(effectiveParams, { silent: true });
        return { found: true, page: focus.page };
    }

    /**
     * Notification deep-link: locate an informed row page then load that page.
     * @param {{ entityId?: string, pageSize?: number, listQuery?: object }} [params]
     */
    async locateInformedRowInList({ entityId, pageSize = 15, listQuery = {} } = {}) {
        if (!entityId) return { found: false };

        const focusRes = await this.service.locateInformedInList?.({
            focusEntityId: String(entityId),
            pageSize: pageSize || 15,
        });
        const focus = focusRes?.focus;
        if (!focus?.found || !focus.page) {
            return { found: false };
        }

        const store = usePendingChangeStore.getState();
        const effectiveParams = {
            ...store.informedSearchParams,
            page: focus.page,
            pageSize: pageSize || 15,
            search: listQuery.search ?? store.informedSearchParams?.search ?? '',
            ...(listQuery.extraQuery || {}),
        };
        await this.fetchMyInformed(effectiveParams, { silent: true });
        return { found: true, page: focus.page };
    }

    async ensureExecutionDraft() {
        return {
            success: false,
            error: 'Taslak yÃ¼rÃ¼tme APIâ€™si kaldÄ±rÄ±ldÄ±; workflow gÃ¶revleri Ã¼zerinden ilerleyin.',
        };
    }

    _getErrorMessage(error) {
        if (error.isValidationError?.()) return 'Please check the form data';
        return error.message || 'An error occurred';
    }
}
